// @flow
import getLogger from "common/logger";
import createBoundActionCreators, {
	createCustomCreator,
	createDispatcher,
} from "common/actionsBase";
import rootBindActions, { types as rootTypes, addNotification } from "root/actions";
import notifications, { NOTIFICATION_IDS } from "root/data/NotificationsData";
import { fileSelect } from "tabs/common/actionMethods";
import { selectCloudName } from "root/selectors";
import getInstagram from "../service";
import {
	selectAuthData,
	selectIsIdSelected,
	selectReachedMaxSelection,
	selectCurrentContext,
	selectContextItems,
	selectMultipleIdsInRange,
	selectAvailableUntilMax,
} from "../selectors";

type authInfoType = { token: ?string, cloudName: string, };
type selectParams = { extend?: ?boolean, cloudName?: string, items?: ?Object[], url?: ?string, };

const logger = getLogger();

export const types = {
	INIT_AUTH: "INSTA_INIT_AUTH",
	SET_AUTH_INFO: "INSTA_SET_AUTH_INFO",
	SET_FILE_SELECTION: "INSTA_SET_FILE_SELECTION",
	SET_MULTI_FILE_SELECTION: "INSTA_SET_MUTLI_FILE_SELECTION",
	GET_CONTEXT_PHOTOS: "INSTA_GET_CONTEXT_PHOTOS",
	SET_PHOTOS_RETRIEVE_START: "INSTA_SET_PHOTOS_RETRIEVE_START",
	SET_PHOTOS_RETRIEVE_FAILED: "INSTA_SET_PHOTOS_RETRIEVE_FAILED",
	SET_PHOTOS_RETRIEVE_SUCCESS: "INSTA_SET_PHOTOS_RETRIEVE_SUCCESS",
	SET_ACCOUNT_INFO_REQUESTED: "INSTA_SET_ACCOUNT_INFO_REQUESTED",
	SET_ACCOUNT_INFO_FAILED: "INSTA_SET_ACCOUNT_INFO_FAILED",
	SET_ACCOUNT_INFO_SUCCESS: "INSTA_SET_ACCOUNT_INFO_SUCCESS",
	SET_USER_LOGOUT_SUCCESS: "INSTA_SET_USER_LOGOUT_SUCCESS",
	DO_LOGOUT: "INSTA_DO_LOGOUT",
	CLEAR_PHOTOS_FOR_CONTEXT: "INSTA_CLEAR_PHOTOS_FOR_CONTEXT",
	CLEAR_IMAGE_SELECTION: "INSTA_CLEAR_IMAGE_SELECTION",
};

const logout = (cloudName: string, dispatch: Function, error = false) => {
	logger.log("[instagramTab.actions]: about to log out instagram user");
	const service = getInstagram();
	service.logout(cloudName);
	service.getTtlHandler().clearTimer();

	if (error) {
		addNotification(notifications[NOTIFICATION_IDS.SERVICE_LOGGED_OUT], dispatch);
	}
};

const getCurrentAccount = async (authInfo: authInfoType, dispatch: Function, dispatcher: Function) => {
	logger.log("[instagramTab.actions]: about to retrieve account info");

	try {
		dispatcher(types.SET_ACCOUNT_INFO_REQUESTED);

		const account = await getInstagram().getAccountInfo(authInfo);
		logger.log("[instagramTab.actions]: retrieved account info: ", account);

		dispatcher(types.SET_ACCOUNT_INFO_SUCCESS, { account });
	} catch (err) {
		logger.error("[instagramTab.actions]: failed to get account info", err);
		logout(authInfo.cloudName, dispatch, true);
		dispatcher(types.SET_ACCOUNT_INFO_FAILED);
	}
};

const setAuthInfo = (authInfo: authInfoType, dispatch: Function, getState: Function, dispatcher: Function) => {
	let result;

	dispatcher(types.SET_AUTH_INFO, authInfo);

	if (authInfo && authInfo.token) {
		getInstagram().getTtlHandler().startTimer();
		result = getCurrentAccount(authInfo, dispatch, dispatcher);
	}

	return result || Promise.resolve(); // returning for testing purposes
};

export default createBoundActionCreators(types, {

	[types.CLEAR_IMAGE_SELECTION]: createCustomCreator((context: string, dispatch: Function) => {
		createDispatcher(dispatch)([
			[types.CLEAR_IMAGE_SELECTION, { context }],
			[rootTypes.CLEAR_MAX_SELECT_REACHED]]);
	}),

	[types.INIT_AUTH]: createCustomCreator((cloudName, dispatch: Function, getState: Function, dispatcher: Function) => {
		dispatcher(types.INIT_AUTH);
		return setAuthInfo(getInstagram().getAuthInfo(cloudName), dispatch, getState, dispatcher);
	}),

	[types.SET_AUTH_INFO]: createCustomCreator(setAuthInfo),

	[types.GET_CONTEXT_PHOTOS]: createCustomCreator(
		async (context: string, params: selectParams = {}, dispatch: Function, getState: Function) => {
			const dispatcher = createDispatcher(dispatch);
			const state = getState();
			const authData = selectAuthData(state);

			if (authData.isAuth) {
				dispatcher(types.SET_PHOTOS_RETRIEVE_START, { context });

				const cloudName = selectCloudName(state);

				let data = null;

				if (params.extend) {
					const contextData = selectContextItems(state);
					const url = (params.extend && contextData.nextUrl) || null;

					params = {
						...params,
						url,
						items: contextData.items,
					};
				} else {
					dispatcher(types.CLEAR_PHOTOS_FOR_CONTEXT, { context });
				}

				try {
					data = await getInstagram().getMedia({ ...params, cloudName });
				} catch (err) {
					logger.error("[instagramTab.actions]: encountered error when trying to retrieve context photos", err);
					dispatcher(types.SET_PHOTOS_RETRIEVE_FAILED, { context });

					const ttlLeft = getInstagram().getTtlHandler().getTtlInMinutes();

					if (~err.message.indexOf("access_token provided is invalid") || ttlLeft <= 0) {
						logout(cloudName, dispatch, true);
						dispatcher(types.SET_USER_LOGOUT_SUCCESS);
					}
				}

				if (data) {
					logger.log(`[instagramTab.actions]: finished retrieving photos for context: ${context}`);
					dispatcher(types.SET_PHOTOS_RETRIEVE_SUCCESS, { context, ...data });
				}
			}
		},
	),

	[types.SET_FILE_SELECTION]: createCustomCreator(
		({ id, e, multiple }: { id: string, e: MouseEvent, multiple: boolean },
			dispatch: Function, getState: Function) => {
			const { context } = selectCurrentContext(getState());

			fileSelect({
				id,
				e,
				types: { ...rootTypes, ...types },
				rootBindActions,
				selectIsIdSelected,
				selectReachedMaxSelection,
				selectMultipleIdsInRange,
				selectAvailableUntilMax,
				selectionParams: [context],
				multiple,
			}, dispatch, getState);
		},
	),

	[types.DO_LOGOUT]: createCustomCreator(
		(dispatch: Function, getState: Function, dispatcher: Function) => {
			const authData = selectAuthData(getState());

			logout(authData.cloudName, dispatch);
			dispatcher(types.SET_USER_LOGOUT_SUCCESS);
		},
	),
});
